import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import AthleteContextHeader from '../components/AthleteContextHeader';
import AthleteSearchFilter from '../components/AthleteSearchFilter';
import TargetAudienceSelector from '../components/TargetAudienceSelector';
import { shouldLimitForContractFixture } from '../lib/visibility';
import { Users, Share } from 'lucide-react';
import { DatePicker } from '../components/ui/date-picker';

const StaffDocuments = ({ athleteId: propAthleteId }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isScopedMode = !!propAthleteId;
  const [athletes, setAthletes] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Upload form state
  const [selectedFile, setSelectedFile] = useState(null);
  const [category, setCategory] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [notes, setNotes] = useState('');

  // Shared upload state (tag/squad/multi-athlete fan-out)
  const [tags, setTags] = useState([]);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const emptyShare = {
    file: null,
    category: 'Other',
    expiration_date: '',
    notes: '',
    target: { target_type: 'custom', target_squads: [], target_athletes: [], target_tag_ids: [] },
  };
  const [share, setShare] = useState(emptyShare);

  useEffect(() => {
    if (propAthleteId) {
      const token = localStorage.getItem('token');
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/athletes/${propAthleteId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.json()).then(data => {
        setAthletes([data]);
        setSelectedAthlete(data);
        setLoading(false);
      }).catch(() => { setLoading(false); });
    } else {
      fetchMyAthletes();
    }
    // Tags are shared across any role with read access — safe to fetch once
    const token = localStorage.getItem('token');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tags`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(r => r.ok ? r.json() : []).then(data => setTags(data || [])).catch(() => {});
  }, [propAthleteId]);

  useEffect(() => {
    if (selectedAthlete) {
      fetchDocuments();
    }
  }, [selectedAthlete]);

  const fetchMyAthletes = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/athletes?status=active`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAthletes(data);
      }
    } catch (error) {
      console.error('Error fetching athletes:', error);
      toast.error('Failed to load athletes');
    } finally {
      setLoading(false);
    }
  };

  const handleSharedUpload = async () => {
    if (!share.file) { toast.error('Select a file first'); return; }
    const t = share.target || {};
    const nothingSelected = (
      (t.target_type || 'custom') !== 'all' &&
      (t.target_squads || []).length === 0 &&
      (t.target_athletes || []).length === 0 &&
      (t.target_tag_ids || []).length === 0
    );
    if (nothingSelected) {
      toast.error('Pick at least one squad, tag, or athlete');
      return;
    }

    setShareBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', share.file);
      fd.append('category', share.category || 'Other');
      if (share.expiration_date) fd.append('expiration_date', share.expiration_date);
      if (share.notes) fd.append('notes', share.notes);
      fd.append('target_type', t.target_type || 'custom');
      fd.append('target_squads', JSON.stringify(t.target_squads || []));
      fd.append('target_athletes', JSON.stringify(t.target_athletes || []));
      fd.append('target_tag_ids', JSON.stringify(t.target_tag_ids || []));

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/upload-shared`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: fd,
        }
      );
      if (response.ok) {
        const data = await response.json();
        toast.success(`Shared with ${data.athlete_count} athlete${data.athlete_count > 1 ? 's' : ''}`);
        setShareDialogOpen(false);
        setShare(emptyShare);
        if (selectedAthlete) fetchDocuments();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to upload shared document');
      }
    } catch (e) {
      toast.error('Failed to upload shared document');
    } finally {
      setShareBusy(false);
    }
  };

  const fetchDocuments = async () => {
    if (!selectedAthlete) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/athlete/${selectedAthlete.id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        let list = Array.isArray(data) ? data : [];
        // UX guard: Admin/Staff viewing Test Player see only the 3 most recent docs.
        if (shouldLimitForContractFixture(selectedAthlete?.id) && list.length > 3) {
          list = [...list]
            .sort((a, b) => new Date(b.upload_date || 0).getTime() - new Date(a.upload_date || 0).getTime())
            .slice(0, 3);
        }
        setDocuments(list);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !category) {
      toast.error('Please select a file and category');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('athlete_id', selectedAthlete.id);
    formData.append('category', category);
    if (expirationDate) {
      formData.append('expiration_date', expirationDate);
    }
    if (notes) {
      formData.append('notes', notes);
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/upload`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        }
      );

      if (response.ok) {
        toast.success('Document uploaded successfully');
        setUploadDialogOpen(false);
        resetUploadForm();
        fetchDocuments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload document');
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setSelectedFile(null);
    setCategory('');
    setExpirationDate('');
    setNotes('');
  };

  const handleDownload = async (doc) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = window.document.createElement('a');
        a.href = url;
        a.download = doc.filename || 'download';
        window.document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        window.document.body.removeChild(a);
        toast.success('Document downloaded');
      } else {
        const errData = await response.json().catch(() => ({}));
        toast.error(errData.detail || 'Failed to download document');
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  const handleDelete = async (document) => {
    if (!window.confirm(`Are you sure you want to delete "${document.filename}"?`)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/${document.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        toast.success('Document deleted successfully');
        fetchDocuments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete document');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Contract': 'bg-blue-100 text-blue-800 border-blue-200',
      'Medical Record': 'bg-red-100 text-red-800 border-red-200',
      'Certificate': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'ID Document': 'bg-purple-100 text-purple-800 border-purple-200',
      'Other': 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700'
    };
    return colors[category] || colors['Other'];
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const isExpired = (expirationDate) => {
    if (!expirationDate) return false;
    return new Date(expirationDate) < new Date();
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <AthleteContextHeader athleteId={propAthleteId} moduleName="Documents" />
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Documents & Files
            </h2>
            <p className="text-gray-600 dark:text-gray-500">Manage athlete documents and certificates</p>
          </div>
          {(user?.role === 'Admin' || user?.role === 'Staff') && (
            <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-pfap-green text-pfap-green hover:bg-pfap-green/10" data-testid="share-doc-btn">
                  <Share size={14} className="mr-1.5" />
                  Share with multiple
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg" data-testid="share-doc-dialog">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><Users size={18} /> Share document</DialogTitle>
                  <DialogDescription>
                    Upload one file and fan it out to many athletes via team, tag, or individual selection. Inactive athletes are excluded automatically.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>File</Label>
                    <Input
                      type="file"
                      onChange={e => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        if (f.size > 10 * 1024 * 1024) { toast.error('Max 10MB'); return; }
                        setShare(s => ({ ...s, file: f }));
                      }}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                      data-testid="share-doc-file"
                    />
                    {share.file && (
                      <p className="text-xs text-gray-500 mt-1">{share.file.name} ({formatFileSize(share.file.size)})</p>
                    )}
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={share.category} onValueChange={v => setShare(s => ({ ...s, category: v }))}>
                      <SelectTrigger data-testid="share-doc-category"><SelectValue placeholder="Pick a category" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Contract">Contract</SelectItem>
                        <SelectItem value="Medical Record">Medical Record</SelectItem>
                        <SelectItem value="Certificate">Certificate</SelectItem>
                        <SelectItem value="ID Document">ID Document</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Expiration (optional)</Label>
                      <DatePicker value={share.expiration_date} onChange={v => setShare(s => ({ ...s, expiration_date: v }))} testId="share-expiration-date" />
                    </div>
                  </div>
                  <div>
                    <Label>Notes (optional)</Label>
                    <Textarea rows={2} value={share.notes} onChange={e => setShare(s => ({ ...s, notes: e.target.value }))} />
                  </div>
                  <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                    <TargetAudienceSelector
                      athletes={athletes}
                      tags={tags}
                      value={share.target}
                      onChange={val => setShare(s => ({ ...s, target: val }))}
                      label="Share with"
                      data-testid="share-doc-target"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShareDialogOpen(false)} data-testid="share-doc-cancel">Cancel</Button>
                  <Button onClick={handleSharedUpload} disabled={shareBusy || !share.file} className="bg-pfap-green hover:bg-pfap-green/90 text-white" data-testid="share-doc-submit">
                    {shareBusy ? 'Sharing…' : 'Share document'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Athletes Sidebar */}
          {!isScopedMode && (
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">My Athletes</CardTitle>
              </CardHeader>
              <CardContent>
                <AthleteSearchFilter athletes={athletes} compact searchFirst>
                  {(filtered) => (
                    <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                      {filtered.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-3">No athletes match</p>
                      ) : (
                        filtered.map(athlete => (
                          <button
                            key={athlete.id}
                            onClick={() => setSelectedAthlete(athlete)}
                            className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-sm ${
                              selectedAthlete?.id === athlete.id
                                ? 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200 font-medium'
                                : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                          >
                            <span>{athlete.full_name}</span>
                            {athlete.squad && <span className="text-[10px] text-gray-400 ml-1">{athlete.squad}</span>}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </AthleteSearchFilter>
              </CardContent>
            </Card>
          </div>
          )}

          {/* Documents Area */}
          <div className={isScopedMode ? "lg:col-span-4" : "lg:col-span-3"}>
            {!selectedAthlete ? (
              <Card>
                <CardContent className="py-12">
                  <p className="text-center text-gray-500 dark:text-gray-500">Select an athlete to view documents</p>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold">
                    Documents for {selectedAthlete.full_name}
                  </h3>
                  <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-pfap-green hover:bg-pfap-green/90">
                        + Upload Document
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Upload Document</DialogTitle>
                        <DialogDescription>
                          Upload a new document for {selectedAthlete.full_name}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="file">File</Label>
                          <Input
                            id="file"
                            type="file"
                            onChange={handleFileChange}
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                          />
                          {selectedFile && (
                            <p className="text-sm text-gray-600 dark:text-gray-500 mt-1">
                              {selectedFile.name} ({formatFileSize(selectedFile.size)})
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="category">Category</Label>
                          <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Contract">Contract</SelectItem>
                              <SelectItem value="Medical Record">Medical Record</SelectItem>
                              <SelectItem value="Certificate">Certificate</SelectItem>
                              <SelectItem value="ID Document">ID Document</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="expiration">Expiration Date (Optional)</Label>
                          <DatePicker value={expirationDate} onChange={setExpirationDate} testId="doc-expiration-date" />
                        </div>
                        <div>
                          <Label htmlFor="notes">Notes (Optional)</Label>
                          <Textarea
                            id="notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Add any notes about this document..."
                          />
                        </div>
                        <Button
                          onClick={handleUpload}
                          disabled={uploading || !selectedFile || !category}
                          className="w-full"
                        >
                          {uploading ? 'Uploading...' : 'Upload Document'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>

                {documents.length === 0 ? (
                  <Card>
                    <CardContent className="py-12">
                      <p className="text-center text-gray-500 dark:text-gray-500">No documents yet</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {documents.map(doc => (
                      <Card key={doc.id}>
                        <CardContent className="py-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold">{doc.filename}</h4>
                                <Badge className={getCategoryColor(doc.category)}>
                                  {doc.category}
                                </Badge>
                                {doc.expiration_date && isExpired(doc.expiration_date) && (
                                  <Badge className="bg-red-100 text-red-800 border-red-200">
                                    Expired
                                  </Badge>
                                )}
                                {doc.version > 1 && (
                                  <Badge variant="outline">v{doc.version}</Badge>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-500">
                                <p>Uploaded: {formatDate(doc.upload_date)}</p>
                                <p>Size: {formatFileSize(doc.file_size)}</p>
                                <p>By: {doc.uploaded_by_name}</p>
                                {doc.expiration_date && (
                                  <p>Expires: {formatDate(doc.expiration_date)}</p>
                                )}
                              </div>
                              {doc.notes && (
                                <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 italic">{doc.notes}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownload(doc)}
                              >
                                Download
                              </Button>
                              {(user?.role === 'Admin' || user?.role === 'Staff') && (
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleDelete(doc)}
                                  data-testid={`delete-doc-${doc.id}`}
                                >
                                  Delete
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default StaffDocuments;
